import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  Cloud,
  Compass,
  Cpu,
  CreditCard,
  Database,
  Download,
  EyeOff,
  FileText,
  Flag,
  Gauge,
  Handshake,
  HeartPulse,
  HelpCircle,
  History,
  LifeBuoy,
  Lightbulb,
  Lock,
  Mail,
  Map,
  Menu,
  MessageCircle,
  Megaphone,
  Newspaper,
  PlayCircle,
  RefreshCcw,
  Route,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TimerReset,
  Trash2,
  TrendingUp,
  Trophy,
  User,
  Users,
  Watch,
  WifiOff,
  X,
  Zap
} from "lucide-react";

const navItems = [
  { label: "Product", href: "/product" },
  { label: "Plans", href: "/plans" },
  { label: "Coaching", href: "/coaching" },
  { label: "Privacy", href: "/privacy" },
  { label: "Blog", href: "/blog" },
  { label: "Support", href: "/support" }
];

const aliases = {
  "/features": "/product",
  "/pricing": "/plans"
};

const pageMeta = {
  "/": {
    title: "Running Logs - Train with clarity. Run with purpose.",
    description:
      "A private running log and adaptive training coach for people who want to run smarter, stay consistent, and understand progress."
  },
  "/product": {
    title: "Product - Running Logs",
    description:
      "See adaptive training plans, daily run logging, progress insights, privacy-first controls, and watch-ready running tools."
  },
  "/plans": {
    title: "Plans - Running Logs",
    description: "Simple, transparent Running Logs plans for runners who want private training structure."
  },
  "/coaching": {
    title: "Coaching - Running Logs",
    description:
      "How the Running Logs coach builds weekly plans, adapts after completed runs, and keeps training gradual."
  },
  "/privacy": {
    title: "Privacy - Running Logs",
    description:
      "Your runs, your data, your choice. Learn how Running Logs handles Health, location, exports, and deletion controls."
  },
  "/blog": {
    title: "Blog and Guides - Running Logs",
    description: "Training guides, product education, privacy notes, and watch setup help for runners."
  },
  "/support": {
    title: "Support - Running Logs",
    description: "Help center topics for getting started, plans, workouts, subscriptions, privacy, and troubleshooting."
  },
  "/contact": {
    title: "Contact - Running Logs",
    description: "Contact Running Logs for support, billing, product feedback, partnerships, press, and general questions."
  },
  "/login": {
    title: "Log In - Running Logs",
    description: "Existing Running Logs users can find account and app access guidance."
  },
  "/start": {
    title: "Join the Running Logs Waitlist",
    description: "Running Logs is coming soon. Join the email list for early access updates."
  }
};

function normalizePath(pathname) {
  const cleaned = pathname.replace(/\/+$/, "") || "/";
  return aliases[cleaned] || cleaned;
}

function navigateTo(href) {
  if (href.startsWith("http") || href.startsWith("mailto:")) {
    window.location.href = href;
    return;
  }

  if (window.location.pathname !== href) {
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
}

function SiteLink({ href, className, children, onClick, ariaLabel }) {
  const isInternal = href.startsWith("/");

  return (
    <a
      href={href}
      className={className}
      aria-label={ariaLabel}
      onClick={(event) => {
        if (isInternal && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
          event.preventDefault();
          navigateTo(href);
          onClick?.();
        }
      }}
    >
      {children}
    </a>
  );
}

function App() {
  const [route, setRoute] = useState(() => normalizePath(window.location.pathname));
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleRoute = () => {
      setRoute(normalizePath(window.location.pathname));
      setMenuOpen(false);
    };

    window.addEventListener("popstate", handleRoute);
    return () => window.removeEventListener("popstate", handleRoute);
  }, []);

  useEffect(() => {
    const meta = pageMeta[route] || pageMeta["/"];
    document.title = meta.title;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", meta.description);
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute("href", `https://www.runninglogs.com${route === "/" ? "/" : route}`);
  }, [route]);

  const Page = routes[route] || NotFoundPage;

  return (
    <div className="app-shell">
      <Header route={route} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <main>
        <Page />
      </main>
      <Footer />
    </div>
  );
}

function Header({ route, menuOpen, setMenuOpen }) {
  return (
    <header className="site-header">
      <div className="container nav-shell">
        <SiteLink href="/" className="brand" ariaLabel="Running Logs home">
          <LogoMark />
          <span>Running Logs</span>
        </SiteLink>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <SiteLink
              key={item.href}
              href={item.href}
              className={route === item.href ? "nav-link active" : "nav-link"}
            >
              {item.label}
            </SiteLink>
          ))}
        </nav>

        <div className="nav-actions">
          <SiteLink href="/login" className="login-link">
            Log in
          </SiteLink>
          <SiteLink href="/start" className="button button-primary button-small">
            Start Free Trial
          </SiteLink>
          <button
            className="icon-button mobile-menu-button"
            type="button"
            aria-label="Toggle navigation menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-menu container">
          {navItems.map((item) => (
            <SiteLink
              key={item.href}
              href={item.href}
              className={route === item.href ? "mobile-nav-link active" : "mobile-nav-link"}
            >
              {item.label}
            </SiteLink>
          ))}
          <div className="mobile-menu-actions">
            <SiteLink href="/login" className="button button-secondary">
              Log in
            </SiteLink>
            <SiteLink href="/start" className="button button-primary">
              Start Free Trial <ArrowRight size={17} />
            </SiteLink>
          </div>
        </div>
      )}
    </header>
  );
}

function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" role="img">
        <path d="M8 32C16 32 22 26 25 19C28 13 32 9 41 9" />
        <path d="M11 39C21 38 30 33 38 24" />
        <path d="M10 25H22" />
        <path d="M16 18H28" />
      </svg>
    </span>
  );
}

function HomePage() {
  return (
    <>
      <HeroSection
        eyebrow="Your runs. Understood."
        title={
          <>
            Train with clarity. <em>Run with purpose.</em>
          </>
        }
        copy="Running Logs is the private running app that helps you build better habits, follow adaptive training, and see real long-term progress without a noisy social feed."
        primaryHref="/start"
        secondaryHref="/product"
        secondaryText="See How It Works"
        media={<ProductShowcase mode="hero" />}
      />

      <TrustBand />

      <section className="section">
        <div className="container split-intro">
          <div>
            <p className="eyebrow">What it does</p>
            <h2>
              Your complete <em>running companion.</em>
            </h2>
          </div>
          <p>
            From daily runs to long-term goals, Running Logs keeps every training decision
            connected to the work you have already done and the life you are training inside.
          </p>
        </div>
        <div className="container benefit-row">
          {coreBenefits.map((benefit) => (
            <BenefitItem key={benefit.title} {...benefit} />
          ))}
        </div>
      </section>

      <section className="section section-tight">
        <div className="container card-grid five-col">
          {featureCards.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      <section className="section section-tight">
        <div className="container editorial-band">
          <div className="runner-panel">
            <div className="runner-stripes" aria-hidden="true" />
            <div className="runner-stats">
              <span>4 week trend</span>
              <strong>+18%</strong>
              <small>easy volume</small>
            </div>
          </div>
          <div className="quote-panel">
            <p className="quote">
              "A serious training log should make the next run feel obvious. That is the
              product standard Running Logs is built around."
            </p>
            <div className="quote-meta">
              <span>Product principle</span>
              <span>Private by default</span>
            </div>
          </div>
          <RatingBlock />
        </div>
      </section>

      <FinalCta
        title="Start your running journey today."
        copy="7-day free trial. No credit card required."
        points={["Adaptive training plans", "Advanced progress insights", "Private and secure by design"]}
      />
    </>
  );
}

function ProductPage() {
  return (
    <>
      <HeroSection
        eyebrow="Product"
        title={
          <>
            See every run <em>more clearly.</em>
          </>
        }
        copy="Running Logs connects plans, completed workouts, recovery context, and progress trends so you can focus on the next useful step."
        primaryHref="/start"
        secondaryHref="#how-it-works"
        secondaryText="See How It Works"
        media={<ProductShowcase mode="annotated" />}
      />

      <TrustBand compact />

      <section className="section">
        <div className="container section-heading">
          <p className="eyebrow">Feature tour</p>
          <h2>Everything points back to better training decisions.</h2>
        </div>
        <div className="container card-grid five-col">
          {featureCards.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      <section className="section section-bordered" id="how-it-works">
        <div className="container steps-layout">
          <div>
            <p className="eyebrow">How it works</p>
            <h2>
              Three simple steps to <em>better running.</em>
            </h2>
          </div>
          <StepItem number="1" icon={Route} title="Log your run" copy="Record GPS, pace, heart rate, elevation, RPE, and notes in seconds." />
          <StepItem number="2" icon={BarChart3} title="Get insights" copy="See trends, training load, consistency, and what changed after each effort." />
          <StepItem number="3" icon={Target} title="Train smarter" copy="Follow a plan that adjusts to completed runs, goals, schedule, and safety signals." />
        </div>
      </section>

      <section className="section">
        <div className="container comparison-layout">
          <div className="comparison-copy">
            <p className="eyebrow">Built for runners</p>
            <h2>Designed for focus. Built for progress.</h2>
            <p>
              Running Logs is not a social network with a training feature attached. It is a
              private training system built around plans, logs, and useful interpretation.
            </p>
          </div>
          <ComparisonMatrix />
        </div>
      </section>

      <FaqSection faqs={productFaqs} />

      <FinalCta
        title="Start your running journey today."
        copy="Join Running Logs and turn completed runs into clearer decisions."
        points={["7-day free trial", "No credit card", "Cancel anytime"]}
      />
    </>
  );
}

function PlansPage() {
  const [annual, setAnnual] = useState(true);
  const plans = useMemo(() => pricingPlans(annual), [annual]);

  return (
    <>
      <PageHero
        eyebrow="Plans and pricing"
        title={
          <>
            Choose the plan that fits <em>your running.</em>
          </>
        }
        copy="Simple, transparent pricing. No hidden fees, no social-data tradeoff, and no pressure to train louder than you want to."
      >
        <div className="billing-toggle" role="group" aria-label="Billing period">
          <span className={!annual ? "active" : ""}>Monthly</span>
          <button
            type="button"
            aria-label="Toggle annual billing"
            aria-pressed={annual}
            onClick={() => setAnnual((value) => !value)}
            className={annual ? "toggle active" : "toggle"}
          >
            <span />
          </button>
          <span className={annual ? "active" : ""}>Annual</span>
          <small>Save up to 20%</small>
        </div>
      </PageHero>

      <section className="section section-tight">
        <div className="container pricing-grid">
          {plans.map((plan) => (
            <PricingCard key={plan.name} plan={plan} />
          ))}
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <h2 className="compact-heading">Compare features</h2>
          <PricingComparison />
        </div>
      </section>

      <section className="section section-tight">
        <div className="container trust-panels">
          <TrustPanel icon={ShieldCheck} title="7-day free trial" copy="Full access to Pro or Pro+. Cancel anytime." />
          <TrustPanel icon={Download} title="Export your data" copy="Training belongs to you. Keep a local copy when you need one." />
          <TrustPanel icon={Lock} title="Privacy first" copy="No ad network tracking and no public-by-default running profile." />
        </div>
      </section>

      <FinalCta
        title="Ready to elevate your running?"
        copy="Start with a clear plan, log the work, and let your progress guide the next week."
        points={["7-day free trial", "No credit card", "Cancel anytime"]}
      />
    </>
  );
}

function CoachingPage() {
  return (
    <>
      <HeroSection
        eyebrow="Adaptive coaching"
        title={
          <>
            A smarter plan for <em>every week.</em>
          </>
        }
        copy="Running Logs builds training around your goal, baseline, schedule, recent workouts, and recovery context. It gives guidance without pretending to be a doctor or a human coach."
        primaryHref="/start"
        secondaryHref="/privacy"
        secondaryText="Review Privacy"
        media={<CoachEngineVisual />}
      />

      <section className="section">
        <div className="container section-heading">
          <p className="eyebrow">What the coach does</p>
          <h2>Useful structure, updated by the work you actually complete.</h2>
        </div>
        <div className="container card-grid three-col">
          {coachCapabilities.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="section section-bordered">
        <div className="container factor-layout">
          <div>
            <p className="eyebrow">How recommendations work</p>
            <h2>The plan engine starts with plain training inputs.</h2>
            <p>
              The coach reads your goal, recent run history, schedule, training baseline, and
              recovery signals. Then the plan adjusts load gradually instead of chasing a single
              heroic workout.
            </p>
          </div>
          <div className="factor-grid">
            {recommendationFactors.map((factor) => (
              <div className="factor-pill" key={factor}>
                <Check size={15} />
                {factor}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container timeline">
          <div>
            <p className="eyebrow">Plan lifecycle</p>
            <h2>From onboarding to the next recommendation.</h2>
          </div>
          {coachLifecycle.map((item, index) => (
            <div className="timeline-step" key={item.title}>
              <span>{index + 1}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-tight">
        <div className="container safety-band">
          <ShieldCheck size={36} />
          <div>
            <p className="eyebrow">Safety language</p>
            <h2>Training guidance, not medical diagnosis.</h2>
            <p>
              Running Logs can reduce intensity when risk signals appear and can surface a
              conservative training path. It does not diagnose injuries or replace qualified
              medical advice.
            </p>
          </div>
        </div>
      </section>

      <FinalCta
        title="Build the next week with more context."
        copy="Start a private plan that adapts after your runs."
        points={["Goal-aware plans", "Fatigue and readiness context", "Gradual progression"]}
      />
    </>
  );
}

function PrivacyPage() {
  return (
    <>
      <PageHero
        eyebrow="Privacy"
        title={
          <>
            Your runs. Your data. <em>Your choice.</em>
          </>
        }
        copy="Running Logs is built for runners who want training clarity without turning every workout into public content."
      />

      <section className="section section-tight">
        <div className="container privacy-hero-grid">
          <PrivacyConsole />
          <div className="privacy-principles">
            {privacyPrinciples.map((item) => (
              <FeatureCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-bordered">
        <div className="container section-heading">
          <p className="eyebrow">Permissions explained</p>
          <h2>Clear reasons for each permission.</h2>
        </div>
        <div className="container card-grid three-col">
          {permissionCards.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container data-control-grid">
          <div>
            <p className="eyebrow">Data controls</p>
            <h2>Keep an exit path open.</h2>
            <p>
              The product is designed around local-first storage, optional backup, export, and
              deletion controls so your training history stays understandable and portable.
            </p>
          </div>
          {dataControls.map((item) => (
            <TrustPanel key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="section section-tight">
        <div className="container policy-links">
          <div>
            <p className="eyebrow">Plain-language policies</p>
            <h2>Need the legal details?</h2>
            <p>Use these links for policy review or data requests.</p>
          </div>
          <div className="policy-actions">
            <SiteLink href="/privacy" className="button button-secondary">
              Privacy Policy <FileText size={17} />
            </SiteLink>
            <SiteLink href="/privacy" className="button button-secondary">
              Terms <FileText size={17} />
            </SiteLink>
            <SiteLink href="/contact" className="button button-primary">
              Data request <ArrowRight size={17} />
            </SiteLink>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <section className="section hero-section contact-hero">
        <div className="container contact-grid">
          <div className="hero-copy">
            <p className="eyebrow">We are here to help</p>
            <h1>We would love to hear from you.</h1>
            <p>
              Have a question, need help, or want to share feedback? Our team and community
              are here for you.
            </p>
            <ul className="check-list">
              <li>
                <Check size={17} /> Real humans, real support
              </li>
              <li>
                <Check size={17} /> Helpful and timely responses
              </li>
              <li>
                <Check size={17} /> Product feedback is welcome
              </li>
            </ul>
          </div>
          <ContactForm />
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <h2 className="compact-heading">Other ways to reach us</h2>
          <div className="card-grid four-col">
            {contactMethods.map((method) => (
              <ContactCard key={method.title} {...method} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-tight">
        <div className="container">
          <h2 className="compact-heading">Get help and support</h2>
          <div className="card-grid three-col">
            <SupportQuickLink icon={HelpCircle} title="FAQ" copy="Find answers to common questions and quick how-tos." href="/support" cta="View FAQ" />
            <SupportQuickLink icon={BookOpen} title="Help Center" copy="Step-by-step guides for plans, logging, watch setup, and data controls." href="/support" cta="Visit Help Center" />
            <SupportQuickLink icon={Users} title="Community" copy="A quieter place for runners to share tips, questions, and useful feedback." href="/contact" cta="Ask about Community" />
          </div>
        </div>
      </section>

      <section className="section section-tight">
        <div className="container demo-band">
          <div>
            <p className="eyebrow">Let us build better together</p>
            <h2>Request a demo or share your feedback.</h2>
            <p>
              Whether you are a coach, part of a team, or curious about Running Logs, we would
              love to show you around.
            </p>
            <div className="button-row">
              <SiteLink href="mailto:partnerships@runninglogs.com?subject=Running%20Logs%20demo" className="button button-primary">
                Request a Demo
              </SiteLink>
              <SiteLink href="mailto:feedback@runninglogs.com?subject=Running%20Logs%20feedback" className="button button-secondary">
                Share Feedback <MessageCircle size={17} />
              </SiteLink>
            </div>
          </div>
          <div className="demo-visual" aria-hidden="true">
            <RunnerLine />
          </div>
        </div>
      </section>

      <FinalCta
        title="Still have questions? We are here for you."
        copy="Our team is ready to help you on your running journey."
        points={["7-day free trial", "No credit card", "Cancel anytime"]}
      />
    </>
  );
}

function BlogPage() {
  return (
    <>
      <PageHero
        eyebrow="Blog and guides"
        title={
          <>
            Training knowledge without <em>SEO noise.</em>
          </>
        }
        copy="Practical guides for safer training, better logging, privacy-aware data habits, and watch setup."
      />
      <section className="section section-tight">
        <div className="container guide-layout">
          {guideGroups.map((group) => (
            <div className="guide-group" key={group.title}>
              <div className="guide-heading">
                <group.icon size={24} />
                <h2>{group.title}</h2>
              </div>
              <div className="guide-list">
                {group.articles.map((article) => (
                  <ArticleCard key={article.title} article={article} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <FinalCta
        title="Get better training notes in your inbox."
        copy="Short, practical updates from the Running Logs team."
        points={["Training guides", "Product updates", "Privacy notes"]}
      />
    </>
  );
}

function SupportPage() {
  return (
    <>
      <PageHero
        eyebrow="Support"
        title={
          <>
            Help for every part of <em>your training setup.</em>
          </>
        }
        copy="Find answers for onboarding, plans, workout tracking, Apple Watch, subscriptions, privacy, and troubleshooting."
      />
      <section className="section section-tight">
        <div className="container support-grid">
          {supportTopics.map((topic) => (
            <FeatureCard key={topic.title} {...topic} />
          ))}
        </div>
      </section>
      <FaqSection faqs={supportFaqs} />
      <section className="section section-tight">
        <div className="container contact-support-band">
          <LifeBuoy size={34} />
          <div>
            <h2>Still stuck?</h2>
            <p>Send us the details and we will help you get back to training.</p>
          </div>
          <SiteLink href="/contact" className="button button-primary">
            Contact Support <ArrowRight size={17} />
          </SiteLink>
        </div>
      </section>
    </>
  );
}

function LoginPage() {
  return (
    <>
      <PageHero
        eyebrow="Existing users"
        title={
          <>
            Log in to your <em>Running Logs account.</em>
          </>
        }
        copy="The primary account experience lives in the app. Use this page for account access guidance and support while the web account portal is being prepared."
      />
      <section className="section section-tight">
        <div className="container auth-card">
          <div>
            <Lock size={32} />
            <h2>Account access</h2>
            <p>
              Open Running Logs on your iPhone to sign in, manage your plan, or review your
              private training data. If you need account help, contact support.
            </p>
          </div>
          <div className="auth-actions">
            <SiteLink href="/contact" className="button button-primary">
              Contact Support <ArrowRight size={17} />
            </SiteLink>
            <SiteLink href="/privacy" className="button button-secondary">
              Review Privacy <ShieldCheck size={17} />
            </SiteLink>
          </div>
        </div>
      </section>
    </>
  );
}

function StartPage() {
  return (
    <>
      <PageHero
        eyebrow="Coming soon"
        title={
          <>
            Running Logs is almost ready. <em>Join the list.</em>
          </>
        }
        copy="The app is coming soon. Leave your email and we will send early access updates, launch details, and trial availability when Running Logs opens."
      />
      <section className="section section-tight">
        <div className="container start-layout">
          <TrialForm />
          <div className="start-proof">
            <h2>What you will hear about first</h2>
            <div className="start-proof-list">
              {[
                ["Early access", "Be first in line when Running Logs is ready for testers."],
                ["Launch updates", "Get a clear note when the app opens and trial access is available."],
                ["Product notes", "See what is shipping for private plans, run logging, and progress insights."]
              ].map(([title, copy]) => (
                <div key={title}>
                  <Check size={17} />
                  <div>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function NotFoundPage() {
  return (
    <PageHero
      eyebrow="Not found"
      title="This page is not in the training plan."
      copy="Use the navigation to find the product, plans, privacy, support, or contact pages."
    />
  );
}

function HeroSection({ eyebrow, title, copy, primaryHref, secondaryHref, secondaryText, media }) {
  return (
    <section className="section hero-section">
      <div className="container hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{copy}</p>
          <div className="button-row">
            <SiteLink href={primaryHref} className="button button-primary">
              Start Free Trial
            </SiteLink>
            <SiteLink href={secondaryHref} className="button button-secondary">
              {secondaryText} <PlayCircle size={17} />
            </SiteLink>
          </div>
          <ProofChips />
        </div>
        {media}
      </div>
    </section>
  );
}

function PageHero({ eyebrow, title, copy, children }) {
  return (
    <section className="section page-hero">
      <div className="container page-hero-inner">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p>{copy}</p>
        </div>
        {children}
      </div>
    </section>
  );
}

function ProofChips() {
  return (
    <div className="proof-chips">
      <span>
        <Clock3 size={15} /> 7-day free trial
      </span>
      <span>
        <CreditCard size={15} /> No credit card
      </span>
      <span>
        <RefreshCcw size={15} /> Cancel anytime
      </span>
    </div>
  );
}

function ProductShowcase({ mode }) {
  return (
    <div className={mode === "annotated" ? "product-showcase annotated" : "product-showcase"} aria-label="Running Logs app preview">
      {mode === "annotated" && (
        <>
          <Annotation className="annotation annotation-left" title="Adaptive Plans" copy="Plans adjust to goals, schedule, and recent effort." />
          <Annotation className="annotation annotation-top" title="Detailed Logging" copy="Capture the details that make each run meaningful." />
          <Annotation className="annotation annotation-right" title="Watch and Mobile" copy="Sync context across iPhone and Apple Watch." />
        </>
      )}
      <PhoneFrame variant="plan" className="phone-back-left" />
      <PhoneFrame variant="today" className="phone-front" />
      <PhoneFrame variant="progress" className="phone-back-right" />
      {mode === "annotated" && <WatchFrame />}
    </div>
  );
}

function Annotation({ className, title, copy }) {
  return (
    <div className={className}>
      <span>{title}</span>
      <p>{copy}</p>
    </div>
  );
}

function PhoneFrame({ variant, className = "" }) {
  const screen = {
    today: <TodayScreen />,
    plan: <PlanScreen />,
    progress: <ProgressScreen />,
    history: <HistoryScreen />
  }[variant];

  return (
    <div className={`phone-frame ${className}`}>
      <div className="phone-speaker" />
      <div className="phone-screen">{screen}</div>
    </div>
  );
}

function ScreenHeader({ title }) {
  return (
    <div className="screen-header">
      <span>9:41</span>
      <strong>{title}</strong>
      <span />
    </div>
  );
}

function TodayScreen() {
  return (
    <>
      <ScreenHeader title="Today" />
      <div className="metric-hero">
        <span>Today</span>
        <strong>5.02</strong>
        <small>km Easy Run</small>
      </div>
      <div className="mini-metrics">
        <div>
          <span>Duration</span>
          <strong>30:21</strong>
        </div>
        <div>
          <span>Avg pace</span>
          <strong>6:03</strong>
        </div>
        <div>
          <span>Elev.</span>
          <strong>32 m</strong>
        </div>
      </div>
      <RouteMap />
      <div className="phone-card small">
        <span>Coach note</span>
        <p>Keep this one easy. The aerobic work is what unlocks Friday.</p>
      </div>
      <PhoneTabs active="Today" />
    </>
  );
}

function PlanScreen() {
  const workouts = ["Rest", "Easy Run", "Intervals", "Strength", "Long Run", "Rest"];
  return (
    <>
      <ScreenHeader title="Plan" />
      <div className="phone-card">
        <span>This week</span>
        <strong>Build Endurance</strong>
      </div>
      <div className="workout-list">
        {workouts.map((workout, index) => (
          <div className="workout-row" key={`${workout}-${index}`}>
            <span className={`workout-dot dot-${index}`} />
            <div>
              <strong>{workout}</strong>
              <small>{index === 2 ? "6 x 800 m" : index === 4 ? "10 km easy" : "30-45 min"}</small>
            </div>
            <ChevronRight size={14} />
          </div>
        ))}
      </div>
      <PhoneTabs active="Plan" />
    </>
  );
}

function ProgressScreen() {
  return (
    <>
      <ScreenHeader title="Progress" />
      <div className="phone-card progress-total">
        <span>This month</span>
        <strong>52.6 km</strong>
        <small>+18% vs last month</small>
      </div>
      <BarSet />
      <div className="phone-card small">
        <span>Pace trend</span>
        <strong>5:56 /km</strong>
        <SparkLine />
      </div>
      <div className="phone-card small">
        <span>7-day consistency</span>
        <strong>4 / 7 days</strong>
      </div>
      <PhoneTabs active="Progress" />
    </>
  );
}

function HistoryScreen() {
  const rows = [
    ["Easy Run", "5.02 km", "Today"],
    ["Tempo", "7.4 km", "Tue"],
    ["Long Run", "12.0 km", "Sun"]
  ];
  return (
    <>
      <ScreenHeader title="History" />
      <RouteMap compact />
      <div className="history-list">
        {rows.map(([title, value, day]) => (
          <div className="history-row" key={title}>
            <div>
              <strong>{title}</strong>
              <small>{day}</small>
            </div>
            <span>{value}</span>
          </div>
        ))}
      </div>
      <PhoneTabs active="Log" />
    </>
  );
}

function WatchFrame() {
  return (
    <div className="watch-frame">
      <div className="watch-screen">
        <span>Outdoor Run</span>
        <strong>5.02</strong>
        <small>KM</small>
        <div className="watch-metrics">
          <span>30:21</span>
          <span>6:03</span>
        </div>
        <button type="button" aria-label="Play workout preview">
          <PlayCircle size={20} />
        </button>
      </div>
    </div>
  );
}

function RouteMap({ compact = false }) {
  return (
    <div className={compact ? "route-map compact" : "route-map"}>
      <svg viewBox="0 0 180 110" aria-hidden="true">
        <path d="M26 74C42 42 66 39 83 55C96 67 102 28 132 35C160 41 155 83 123 84C92 85 75 95 53 84C42 79 33 82 26 74Z" />
        <circle cx="83" cy="55" r="4" />
      </svg>
    </div>
  );
}

function BarSet() {
  return (
    <div className="bar-set" aria-hidden="true">
      {[44, 60, 38, 74, 54, 82, 48].map((height, index) => (
        <span key={height + index} style={{ height: `${height}%` }} />
      ))}
    </div>
  );
}

function SparkLine() {
  return (
    <svg className="spark-line" viewBox="0 0 140 34" aria-hidden="true">
      <path d="M4 23C22 21 30 18 42 20C57 22 63 11 80 13C95 15 104 10 118 8C128 7 134 9 138 6" />
    </svg>
  );
}

function PhoneTabs({ active }) {
  return (
    <div className="phone-tabs">
      {["Today", "Plan", "Log", "Progress"].map((item) => (
        <span className={active === item ? "active" : ""} key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

function TrustBand({ compact = false }) {
  return (
    <section className={compact ? "trust-band compact" : "trust-band"}>
      <div className="container">
        <p>Built for runners who value private progress</p>
        <div className="trust-logos" aria-label="Product trust signals">
          <span>No social feed</span>
          <span>On-device coach</span>
          <span>HealthKit aware</span>
          <span>Export ready</span>
          <span>Watch friendly</span>
        </div>
      </div>
    </section>
  );
}

function BenefitItem({ icon: Icon, title, copy }) {
  return (
    <article className="benefit-item">
      <Icon size={26} />
      <h3>{title}</h3>
      <p>{copy}</p>
    </article>
  );
}

function FeatureCard({ icon: Icon, title, copy, href = "/product", label = "Learn more" }) {
  return (
    <article className="feature-card">
      <Icon size={28} />
      <h3>{title}</h3>
      <p>{copy}</p>
      <SiteLink href={href} className="text-link">
        {label} <ArrowRight size={15} />
      </SiteLink>
    </article>
  );
}

function RatingBlock() {
  return (
    <div className="rating-block">
      <strong>Private by design</strong>
      <div className="stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((item) => (
          <Star key={item} size={18} fill="currentColor" />
        ))}
      </div>
      <p>No ads. No public leaderboard. No pressure to perform for strangers.</p>
    </div>
  );
}

function StepItem({ number, icon: Icon, title, copy }) {
  return (
    <article className="step-item">
      <span className="step-number">{number}</span>
      <Icon size={32} />
      <div>
        <h3>{title}</h3>
        <p>{copy}</p>
      </div>
    </article>
  );
}

function ComparisonMatrix() {
  const columns = [
    {
      title: "Running Logs",
      active: true,
      items: ["Adaptive training plans", "Detailed run logging", "Progress insights and trends", "Privacy-first by design", "No ads. Ever."]
    },
    {
      title: "Generic Fitness Apps",
      items: ["Generic plans", "Basic logging", "Limited insights", "Data may be shared", "Ads and upsells"]
    },
    {
      title: "Social Fitness Apps",
      items: ["Social feed first", "Public by default", "Surface-level stats", "Social pressure", "Ads and upsells"]
    }
  ];

  return (
    <div className="comparison-matrix">
      {columns.map((column) => (
        <div className={column.active ? "comparison-column active" : "comparison-column"} key={column.title}>
          <h3>{column.title}</h3>
          <ul>
            {column.items.map((item, index) => (
              <li key={item}>
                {column.active ? <Check size={16} /> : index < 2 ? <Check size={16} /> : <X size={16} />}
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function FaqSection({ faqs }) {
  return (
    <section className="section section-tight">
      <div className="container faq-layout">
        <div>
          <p className="eyebrow">Frequently asked questions</p>
          <h2>Common questions, direct answers.</h2>
        </div>
        <div className="faq-list">
          {faqs.map((faq) => (
            <FaqItem key={faq.question} {...faq} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={open ? "faq-item open" : "faq-item"}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{question}</span>
        <ChevronDown size={18} />
      </button>
      {open && <p>{answer}</p>}
    </div>
  );
}

function PricingCard({ plan }) {
  return (
    <article className={plan.featured ? "pricing-card featured" : "pricing-card"}>
      {plan.featured && <span className="plan-badge">Most popular</span>}
      <h2>{plan.name}</h2>
      <p>{plan.description}</p>
      <div className="price-line">
        <strong>{plan.price}</strong>
        <span>{plan.period}</span>
        {plan.was && <small>{plan.was}</small>}
      </div>
      <p className="billing-note">{plan.note}</p>
      <SiteLink href="/start" className={plan.featured ? "button button-primary" : "button button-secondary"}>
        {plan.cta}
      </SiteLink>
      <ul className="plan-features">
        {plan.features.map((feature) => (
          <li key={feature}>
            <Check size={16} /> {feature}
          </li>
        ))}
      </ul>
      <span className="plan-footnote">{plan.footnote}</span>
    </article>
  );
}

function PricingComparison() {
  const rows = [
    ["Log runs and basic stats", "Yes", "Yes", "Yes"],
    ["Training plans", "Up to Half Marathon", "All plan library", "All plan library"],
    ["Progress insights", "Basic", "Advanced", "Advanced + analytics"],
    ["Custom goals and race tracking", "-", "Yes", "Yes"],
    ["Smart reminders", "-", "Yes", "Yes"],
    ["Advanced coach", "-", "-", "Yes"],
    ["Watch and device support", "Limited", "Full support", "Full support + more"],
    ["Data export", "-", "GPX, CSV", "GPX, CSV, FIT"],
    ["Priority support", "-", "Yes", "Yes"]
  ];

  return (
    <div className="table-wrap">
      <table className="pricing-table">
        <thead>
          <tr>
            <th>Feature</th>
            <th>Free</th>
            <th>Pro</th>
            <th>Pro+</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([feature, free, pro, plus]) => (
            <tr key={feature}>
              <td>{feature}</td>
              <td>{free}</td>
              <td>{pro}</td>
              <td>{plus}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TrustPanel({ icon: Icon, title, copy }) {
  return (
    <article className="trust-panel">
      <Icon size={32} />
      <div>
        <h3>{title}</h3>
        <p>{copy}</p>
      </div>
    </article>
  );
}

function CoachEngineVisual() {
  return (
    <div className="coach-visual" aria-label="Adaptive coach logic preview">
      <div className="coach-core">
        <Cpu size={34} />
        <strong>Plan Engine</strong>
        <span>Deterministic training logic</span>
      </div>
      {[
        [Target, "Goal"],
        [Activity, "Baseline"],
        [CalendarDays, "Schedule"],
        [History, "Recent runs"],
        [HeartPulse, "Recovery"],
        [ShieldCheck, "Safety"]
      ].map(([Icon, label], index) => (
        <div className={`coach-node node-${index + 1}`} key={label}>
          <Icon size={22} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function PrivacyConsole() {
  return (
    <div className="privacy-console">
      <div className="console-header">
        <ShieldCheck size={24} />
        <div>
          <strong>Privacy Promise</strong>
          <span>Private by default</span>
        </div>
      </div>
      <div className="console-row">
        <span>Social feed</span>
        <strong>Off</strong>
      </div>
      <div className="console-row">
        <span>Tracking domains</span>
        <strong>None</strong>
      </div>
      <div className="console-row">
        <span>Cloud backup</span>
        <strong>Optional</strong>
      </div>
      <div className="console-row">
        <span>Export data</span>
        <strong>Available</strong>
      </div>
    </div>
  );
}

function ContactForm() {
  const [sent, setSent] = useState(false);

  function handleSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = encodeURIComponent(data.get("subject") || "Support");
    const name = data.get("name") || "";
    const email = data.get("email") || "";
    const message = data.get("message") || "";
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\n${message}`);
    setSent(true);
    window.location.href = `mailto:support@runninglogs.com?subject=${subject}&body=${body}`;
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <h2>Send us a message</h2>
      <label>
        Your name
        <input name="name" placeholder="Jane Doe" required />
      </label>
      <label>
        Email address
        <input name="email" type="email" placeholder="jane@example.com" required />
      </label>
      <label>
        Subject
        <select name="subject" defaultValue="Support">
          <option>Support</option>
          <option>Billing</option>
          <option>Product feedback</option>
          <option>Partnerships</option>
          <option>Press</option>
          <option>Other</option>
        </select>
      </label>
      <label>
        Message
        <textarea name="message" placeholder="Tell us what is on your mind..." rows="5" required />
      </label>
      <button className="button button-primary" type="submit">
        Send Message <Send size={17} />
      </button>
      <p className="form-note">
        <Lock size={14} /> We respect your privacy. Your information stays with this support request.
      </p>
      {sent && <p className="form-success">Opening your email app with the message ready to send.</p>}
    </form>
  );
}

function ContactCard({ icon: Icon, title, copy, email, note }) {
  return (
    <article className="contact-card">
      <Icon size={32} />
      <h3>{title}</h3>
      <p>{copy}</p>
      <SiteLink href={`mailto:${email}`} className="text-link">
        {email}
      </SiteLink>
      <small>{note}</small>
    </article>
  );
}

function SupportQuickLink({ icon: Icon, title, copy, href, cta }) {
  return (
    <article className="support-quick-link">
      <Icon size={34} />
      <div>
        <h3>{title}</h3>
        <p>{copy}</p>
        <SiteLink href={href} className="text-link">
          {cta} <ArrowRight size={15} />
        </SiteLink>
      </div>
    </article>
  );
}

function RunnerLine() {
  return (
    <svg viewBox="0 0 420 220" aria-hidden="true">
      <path d="M18 172C64 136 92 138 128 154C172 174 198 102 246 116C292 129 295 69 344 72C378 74 398 96 408 118" />
      <path d="M34 184C90 167 142 183 190 168C238 153 272 120 322 126C360 130 388 150 405 171" />
      <circle cx="246" cy="116" r="8" />
      <circle cx="344" cy="72" r="8" />
    </svg>
  );
}

function ArticleCard({ article }) {
  return (
    <article className="article-card">
      <span>{article.category}</span>
      <h3>{article.title}</h3>
      <p>{article.copy}</p>
      <SiteLink href="/blog" className="text-link">
        Read guide <ArrowRight size={15} />
      </SiteLink>
    </article>
  );
}

function TrialForm() {
  const [sent, setSent] = useState(false);

  function handleSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email") || "";
    const goal = data.get("goal") || "";
    const body = encodeURIComponent(
      `Please add me to the Running Logs early access list.\n\nEmail: ${email}\nRunning goal: ${goal}`
    );
    setSent(true);
    window.location.href = `mailto:support@runninglogs.com?subject=Running%20Logs%20Early%20Access%20Waitlist&body=${body}`;
  }

  return (
    <form className="trial-form" onSubmit={handleSubmit}>
      <h2>Join the early access list</h2>
      <p>Running Logs is coming soon. Add your email and we will let you know when trial access opens.</p>
      <label>
        Email address
        <input name="email" type="email" placeholder="you@example.com" required />
      </label>
      <label>
        Running goal
        <select name="goal" defaultValue="Build consistency">
          <option>Build consistency</option>
          <option>Train for a 5K</option>
          <option>Train for a half marathon</option>
          <option>Prepare for a marathon</option>
          <option>Return after time off</option>
        </select>
      </label>
      <button type="submit" className="button button-primary">
        Join Waitlist <ArrowRight size={17} />
      </button>
      {sent && <p className="form-success">Opening your email app with the waitlist request ready to send.</p>}
    </form>
  );
}

function FinalCta({ title, copy, points }) {
  return (
    <section className="section section-tight">
      <div className="container final-cta">
        <div>
          <h2>{title}</h2>
          <p>{copy}</p>
        </div>
        <div className="cta-points">
          {points.map((point) => (
            <span key={point}>
              <Check size={15} /> {point}
            </span>
          ))}
        </div>
        <SiteLink href="/start" className="button button-primary">
          Start Free Trial
        </SiteLink>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand">
          <SiteLink href="/" className="brand">
            <LogoMark />
            <span>Running Logs</span>
          </SiteLink>
          <p>The private running app that helps you train smarter, stay consistent, and run with purpose.</p>
          <div className="social-row" aria-label="Social links">
            <SiteLink href="/contact" ariaLabel="Contact Running Logs">
              <Mail size={17} />
            </SiteLink>
            <SiteLink href="/blog" ariaLabel="Running Logs guides">
              <Newspaper size={17} />
            </SiteLink>
            <SiteLink href="/support" ariaLabel="Running Logs support">
              <LifeBuoy size={17} />
            </SiteLink>
          </div>
        </div>
        <FooterColumn
          title="Product"
          links={[
            ["Features", "/product"],
            ["Training Plans", "/coaching"],
            ["Run Logging", "/product"],
            ["Progress Insights", "/product"],
            ["Watch and Mobile", "/product"]
          ]}
        />
        <FooterColumn
          title="Company"
          links={[
            ["About", "/product"],
            ["Blog", "/blog"],
            ["Careers", "/contact"],
            ["Contact", "/contact"],
            ["Press Kit", "/contact"]
          ]}
        />
        <FooterColumn
          title="Support"
          links={[
            ["Help Center", "/support"],
            ["Community", "/contact"],
            ["Status", "/support"],
            ["Privacy Policy", "/privacy"],
            ["Terms of Service", "/privacy"]
          ]}
        />
        <NewsletterForm />
      </div>
      <div className="container footer-bottom">
        <span>© 2026 Running Logs. All rights reserved.</span>
        <span>Made for runners, by runners.</span>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  return (
    <div className="footer-column">
      <h3>{title}</h3>
      {links.map(([label, href]) => (
        <SiteLink key={label} href={href}>
          {label}
        </SiteLink>
      ))}
    </div>
  );
}

function NewsletterForm() {
  const [sent, setSent] = useState(false);

  function handleSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = encodeURIComponent(data.get("email") || "");
    setSent(true);
    window.location.href = `mailto:support@runninglogs.com?subject=Running%20Logs%20newsletter&body=Please%20add%20${email}%20to%20the%20newsletter.`;
  }

  return (
    <form className="newsletter-form" onSubmit={handleSubmit}>
      <h3>Stay in the loop</h3>
      <p>Get tips, updates, and stories from the Running Logs team.</p>
      <label>
        <span>Email address</span>
        <input name="email" type="email" placeholder="Enter your email" required />
        <button type="submit" aria-label="Subscribe to newsletter">
          <ArrowRight size={17} />
        </button>
      </label>
      {sent && <small>Opening your email app to confirm.</small>}
    </form>
  );
}

const coreBenefits = [
  {
    icon: CalendarDays,
    title: "Plan with purpose",
    copy: "Adaptive training plans designed around your goals, schedule, and experience."
  },
  {
    icon: Route,
    title: "Log everything",
    copy: "Fast logging for pace, heart rate, elevation, perceived effort, and notes."
  },
  {
    icon: TrendingUp,
    title: "See your progress",
    copy: "Charts and trends that show what is improving week after week."
  },
  {
    icon: Bell,
    title: "Stay consistent",
    copy: "Smart reminders and streaks that keep momentum practical."
  },
  {
    icon: Lock,
    title: "Privacy-first",
    copy: "No public feed required. Your training history is yours."
  }
];

const featureCards = [
  {
    icon: CalendarDays,
    title: "Adaptive Training Plans",
    copy: "Plans that adapt to fitness, schedule, recovery, and goals without overtraining."
  },
  {
    icon: Route,
    title: "Daily Run Logging",
    copy: "Log runs with GPS, pace, heart rate, elevation, RPE, and notes."
  },
  {
    icon: BarChart3,
    title: "Progress Insights",
    copy: "Trends that turn training data into clear next decisions."
  },
  {
    icon: ShieldCheck,
    title: "Privacy First",
    copy: "Private by default, export ready, and never built around a public feed."
  },
  {
    icon: Watch,
    title: "Watch and Mobile",
    copy: "Built for iPhone and Apple Watch workflows so runs stay easy to capture."
  }
];

function pricingPlans(annual) {
  return [
    {
      name: "Free",
      description: "For getting started.",
      price: "$0",
      period: "/month",
      note: "Always available",
      cta: "Get Started",
      features: ["Log unlimited runs", "Basic stats and progress", "Starter training plans", "Route tracking and maps", "Community support"],
      footnote: "No credit card required"
    },
    {
      name: "Pro",
      description: "For dedicated runners.",
      price: annual ? "$7.49" : "$8.99",
      period: "/month",
      was: annual ? "$8.99" : null,
      note: annual ? "Billed annually at $89.99" : "Billed monthly",
      cta: "Start Free Trial",
      featured: true,
      features: [
        "Everything in Free",
        "Advanced training plans",
        "Progress insights and trends",
        "Custom goals and race tracking",
        "Smart reminders",
        "Export data: GPX and CSV",
        "Priority support"
      ],
      footnote: "7-day free trial. Cancel anytime."
    },
    {
      name: "Pro+",
      description: "For runners who want more.",
      price: annual ? "$12.49" : "$14.99",
      period: "/month",
      was: annual ? "$14.99" : null,
      note: annual ? "Billed annually at $149.99" : "Billed monthly",
      cta: "Start Free Trial",
      features: [
        "Everything in Pro",
        "Advanced coach",
        "Performance analytics",
        "Run power and HR insights",
        "Advanced device metrics",
        "Early access features",
        "Priority support"
      ],
      footnote: "7-day free trial. Cancel anytime."
    }
  ];
}

const productFaqs = [
  {
    question: "Is there a free trial?",
    answer: "Yes. Pro and Pro+ start with a 7-day free trial so you can test plans, insights, and logging before committing."
  },
  {
    question: "Is my data shared?",
    answer: "Running Logs is private by default. There is no public feed, no ad-network tracking, and no public-by-default profile."
  },
  {
    question: "Does it work with Apple Watch?",
    answer: "The product is designed around iPhone and Apple Watch training workflows, including run capture and workout context."
  },
  {
    question: "Can I export my data?",
    answer: "Yes. Paid plans include export paths for common training formats, and the product is designed to keep your history portable."
  },
  {
    question: "Does it work without social sharing?",
    answer: "Yes. Social sharing is not required. Running Logs is built for private progress first."
  }
];

const supportFaqs = [
  {
    question: "How do I start a plan?",
    answer: "Complete onboarding in the app, choose a goal, confirm your weekly schedule, and Running Logs will create the first week."
  },
  {
    question: "What data does Health permission help with?",
    answer: "Health data helps establish training zones, recovery context, and recent workout history. You control permissions on your device."
  },
  {
    question: "Can I delete my data?",
    answer: "Yes. The app is designed with account deletion, export, and local data controls in Settings."
  },
  {
    question: "What if I miss a run?",
    answer: "The plan should adjust around what you completed instead of punishing you for a missed day."
  }
];

const coachCapabilities = [
  {
    icon: CalendarDays,
    title: "Builds your weekly plan",
    copy: "Creates a practical week from your goal, available days, baseline, and long-run needs."
  },
  {
    icon: Target,
    title: "Recommends today's workout",
    copy: "Shows the next best run with enough context to understand why it matters."
  },
  {
    icon: RefreshCcw,
    title: "Adjusts after completed runs",
    copy: "Uses completed work, not wishful plans, to update the next recommendation."
  },
  {
    icon: HeartPulse,
    title: "Tracks fatigue and readiness",
    copy: "Reads recovery signals conservatively so progression stays gradual."
  },
  {
    icon: Shield,
    title: "Helps avoid overtraining",
    copy: "Responds to risk signals with easier work, rest, or safer progressions."
  },
  {
    icon: TrendingUp,
    title: "Keeps progress gradual",
    copy: "Uses load caps and steady training principles instead of sudden heroic jumps."
  }
];

const recommendationFactors = ["Goal", "Baseline", "Schedule", "Recent workouts", "Recovery state", "Safety inputs", "Race date", "Available days", "Long-run history"];

const coachLifecycle = [
  { title: "Onboarding creates your baseline", copy: "Goals, schedule, history, and constraints define the starting point." },
  { title: "Plan engine builds Week 1", copy: "The first week sets useful structure without pretending it knows everything yet." },
  { title: "Today shows the recommendation", copy: "The next workout is presented with simple context and expectations." },
  { title: "Runs update progress", copy: "Completed training, RPE, pace, and recovery feed the next decision." },
  { title: "Plan adapts", copy: "The following week updates around real work and safety signals." }
];

const privacyPrinciples = [
  {
    icon: EyeOff,
    title: "Private by default",
    copy: "No required public profile, leaderboard, or social feed."
  },
  {
    icon: WifiOff,
    title: "On-device where possible",
    copy: "Training guidance is designed to run locally where device capability allows."
  },
  {
    icon: Download,
    title: "Export available",
    copy: "Your running history should stay portable and understandable."
  },
  {
    icon: Trash2,
    title: "Delete controls",
    copy: "Account and local data deletion are core settings, not buried edge cases."
  }
];

const permissionCards = [
  {
    icon: HeartPulse,
    title: "Health",
    copy: "Used for training zones, recovery context, prior runs, and readiness signals."
  },
  {
    icon: Map,
    title: "Location",
    copy: "Used for pace, distance, route maps, elevation context, and completed workout logs."
  },
  {
    icon: Bell,
    title: "Notifications",
    copy: "Used for plan reminders, recovery prompts, and schedule nudges you control."
  }
];

const dataControls = [
  {
    icon: Database,
    title: "Local-first history",
    copy: "The app is designed around local training records with optional backup."
  },
  {
    icon: Cloud,
    title: "Optional cloud backup",
    copy: "Cloud sync is for account continuity, not a public sharing system."
  },
  {
    icon: Download,
    title: "Export summaries",
    copy: "Export training records when you want to review or move your data."
  },
  {
    icon: Trash2,
    title: "Delete account",
    copy: "Use deletion controls when you want your account and cloud records removed."
  }
];

const contactMethods = [
  {
    icon: Mail,
    title: "Support",
    copy: "Questions or need help? We are here for you.",
    email: "support@runninglogs.com",
    note: "Typically replies within 24h"
  },
  {
    icon: Handshake,
    title: "Partnerships",
    copy: "Explore partnerships, integrations, or coaching collaborations.",
    email: "partnerships@runninglogs.com",
    note: "We would love to connect"
  },
  {
    icon: Megaphone,
    title: "Press and Media",
    copy: "For media inquiries, press kits, and interviews.",
    email: "press@runninglogs.com",
    note: "Press kit available"
  },
  {
    icon: Lightbulb,
    title: "Product Feedback",
    copy: "Share ideas or suggest new features.",
    email: "feedback@runninglogs.com",
    note: "Help shape Running Logs"
  }
];

const guideGroups = [
  {
    title: "Training",
    icon: Trophy,
    articles: [
      {
        category: "Training",
        title: "How to build a safe first week",
        copy: "Start with consistency, recovery, and realistic load before adding intensity."
      },
      {
        category: "Training",
        title: "Easy runs vs recovery runs",
        copy: "Why both can be slow, and why they do different jobs in a training plan."
      },
      {
        category: "Training",
        title: "Why consistency beats intensity",
        copy: "The quiet weeks are often the ones that make race-day fitness possible."
      }
    ]
  },
  {
    title: "Product",
    icon: Smartphone,
    articles: [
      {
        category: "Product",
        title: "How Running Logs creates your plan",
        copy: "A plain-English tour of goals, baselines, schedules, recent runs, and recovery."
      },
      {
        category: "Product",
        title: "How to read your training history",
        copy: "Use logs and trends to understand what changed, what worked, and what needs rest."
      },
      {
        category: "Product",
        title: "How coach notes work",
        copy: "Coach language explains training decisions without replacing the plan engine."
      }
    ]
  },
  {
    title: "Privacy",
    icon: ShieldCheck,
    articles: [
      {
        category: "Privacy",
        title: "Why private run logging matters",
        copy: "Training is personal. Your log should not need a public audience to be useful."
      },
      {
        category: "Privacy",
        title: "How to export running data",
        copy: "A practical guide to keeping your training history portable."
      },
      {
        category: "Privacy",
        title: "What HealthKit permissions mean",
        copy: "A runner-friendly explanation of what health permissions are used for."
      }
    ]
  },
  {
    title: "Gear and Watch",
    icon: Watch,
    articles: [
      {
        category: "Watch",
        title: "Apple Watch running setup",
        copy: "What to check before your first workout with Running Logs."
      },
      {
        category: "Watch",
        title: "How to track HR zones",
        copy: "Use heart-rate context without letting a single metric control every decision."
      },
      {
        category: "GPS",
        title: "GPS accuracy tips",
        copy: "Small setup choices that make route and pace data easier to trust."
      }
    ]
  }
];

const supportTopics = [
  {
    icon: Sparkles,
    title: "Getting started",
    copy: "Set up your account, choose units, connect Health, and build your first baseline."
  },
  {
    icon: CalendarDays,
    title: "Plans and workouts",
    copy: "Understand plan phases, today's recommendation, missed runs, and workout changes."
  },
  {
    icon: Route,
    title: "Run tracking",
    copy: "Learn how GPS, pace, route maps, heart rate, notes, and RPE work together."
  },
  {
    icon: Watch,
    title: "Apple Watch",
    copy: "Pair watch workflows, start runs, review battery expectations, and troubleshoot sync."
  },
  {
    icon: CreditCard,
    title: "Subscriptions",
    copy: "Manage Free, Pro, Pro+, trial eligibility, cancellation, and billing support."
  },
  {
    icon: ShieldCheck,
    title: "Privacy and data",
    copy: "Review permissions, export your history, delete data, and understand cloud backup."
  },
  {
    icon: Settings,
    title: "Troubleshooting",
    copy: "Fix common sync, notification, device, login, and training-plan issues."
  },
  {
    icon: MessageCircle,
    title: "Contact support",
    copy: "Send details to the Running Logs team when you need hands-on help.",
    href: "/contact"
  }
];

const routes = {
  "/": HomePage,
  "/product": ProductPage,
  "/plans": PlansPage,
  "/coaching": CoachingPage,
  "/privacy": PrivacyPage,
  "/blog": BlogPage,
  "/support": SupportPage,
  "/contact": ContactPage,
  "/login": LoginPage,
  "/start": StartPage
};

export default App;
